import { DisplayNode, Link } from '../recipe_renderer/types.js'
import type { Simulation } from 'd3-force';

/**
 * Create a repulsion force that respects tree structure.
 *
 * When a node is pushed, its entire subtree (all visible descendants)
 * is displaced by the same amount, so parent-child relationships are
 * preserved as rigid groups.
 *
 * Pairs that are ancestor/descendant are skipped entirely since they
 * belong to the same rigid group and cannot repel each other.
 */
export function createRepulsionForce(strength: number = 3.0) {
  const minDistance = 130; // px — push nodes apart when within this range
  let nodes: DisplayNode[] = [];

  // Map each node index to the set of its descendant indices (within nodes[])
  const descendantMap: Map<number, Set<number>> = new Map();
  let idToIndex: Map<string, number> = new Map();

  /** Rebuild the descendant map from the current node list. */
  function buildDescendantMap() {
    descendantMap.clear();
    idToIndex = new Map();
    nodes.forEach((n, i) => idToIndex.set(n.id, i));

    for (let i = 0; i < nodes.length; i++) {
      const descendants = new Set<number>();
      collectDescendants(nodes[i], descendants);
      descendantMap.set(i, descendants);
    }
  }

  function collectDescendants(node: DisplayNode, acc: Set<number>) {
    for (const childId of (node.data.children || [])) {
      const childIdx = idToIndex.get(childId);
      if (childIdx != null && !acc.has(childIdx)) {
        acc.add(childIdx);
        collectDescendants(nodes[childIdx], acc);
      }
    }
  }

  /** True if node at ancestorIdx is an ancestor of node at descendantIdx. */
  function isAncestor(ancestorIdx: number, descendantIdx: number): boolean {
    return descendantMap.get(ancestorIdx)?.has(descendantIdx) ?? false;
  }

  /** Apply velocity delta (dvx, dvy) to node and all its descendants. */
  function applyDelta(nodeIdx: number, dvx: number, dvy: number) {
    const n = nodes[nodeIdx];
    n.vx = (n.vx || 0) + dvx;
    n.vy = (n.vy || 0) + dvy;

    const descendants = descendantMap.get(nodeIdx);
    if (descendants) {
      for (const descIdx of descendants) {
        const d = nodes[descIdx];
        d.vx = (d.vx || 0) + dvx;
        d.vy = (d.vy || 0) + dvy;
      }
    }
  }

  function force(alpha: number) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];

        // Skip nodes without valid positions
        if (a.x == null || b.x == null || a.y == null || b.y == null) continue;
        if (!isFinite(a.x) || !isFinite(b.x) || !isFinite(a.y) || !isFinite(b.y)) continue;

        // Skip pairs where one is ancestor of the other — same rigid group
        if (isAncestor(i, j) || isAncestor(j, i)) continue;

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < minDistance && dist > 0) {
          const f = (minDistance - dist) / minDistance * strength * alpha;
          const vx = (dx / dist) * f;
          const vy = (dy / dist) * f;

          // Move the entire subtree of a away from b
          applyDelta(i, -vx, -vy);
          // Move the entire subtree of b away from a
          applyDelta(j, vx, vy);
        }
      }
    }
  }

  force.initialize = function (initNodes: DisplayNode[]) {
    nodes = initNodes;
    buildDescendantMap();
  };

  return force;
}

/**
 * Create a simulation for the graph visualizer with stronger repulsion.
 *
 * Features:
 * - Single 2D repulsion force (no hierarchy assumptions)
 * - No centering or link forces — initial layout provides structure
 * - Alpha decay is slow enough to let nodes settle gently
 *
 * @param nodes - The initial set of nodes to simulate
 * @returns A d3 force simulation configured for stronger repulsion
 */
export function createGraphSimulation(
  nodes: DisplayNode[]
): Simulation<DisplayNode, Link<DisplayNode>> {
  const sim = d3.forceSimulation<DisplayNode>(nodes)
    .force('repulsion', createRepulsionForce(3.0))
    .alphaDecay(0.02)     // Slow decay for gentle settling
    .velocityDecay(0.1);  // Lower decay so velocity accumulates

  return sim as unknown as Simulation<DisplayNode, Link<DisplayNode>>;
}

/**
 * Set up the simulation tick handler.
 * On each tick, updates link and node positions in the SVG.
 *
 * @param simulation - The d3 force simulation
 * @param updateFn - Function to call on each tick to update SVG positions
 */
export function setupSimulationTick(
  simulation: Simulation<DisplayNode, Link<DisplayNode>>,
  updateFn: () => void
): void {
  simulation.on('tick', () => {
    updateFn();
  });
}
